import { useRef, useEffect, useCallback } from 'react';

const FOCUS_DELAY_MS = 80;

/** Types that skip the operator step and go straight to value selection. */
const SKIP_OPERATOR_TYPES = new Set(['dropdown', 'product', 'contains_text']);

function focusAntField(ref) {
    const el = ref?.current;
    if (!el) return;

    if (typeof el.focus === 'function') {
        el.focus({ preventScroll: true });
        return;
    }

    const nativeInput = el.input ?? el.querySelector?.('input, textarea');
    nativeInput?.focus({ preventScroll: true });
}

function resolveFocusTarget({ newCol, newOp, effectiveType }) {
    if (!newCol) return 'column';

    const skipOperator = SKIP_OPERATOR_TYPES.has(effectiveType);
    if (!skipOperator && !newOp) return 'operator';

    if (newOp || skipOperator) return 'value';

    return 'column';
}

/**
 * Scrolls the filter form section into view and focuses the active editable field
 * when filter configuration state changes (modal open, column, operator, edit).
 *
 * Presentation-only — does not mutate filter params or table state.
 */
export function useFilterFormAutoFocus({
    active,
    newCol,
    newOp,
    effectiveType,
    focusGeneration = 0,
}) {
    const formSectionRef = useRef(null);
    const columnSelectRef = useRef(null);
    const operatorSelectRef = useRef(null);
    const valueFieldRef = useRef(null);

    const alignAndFocus = useCallback(() => {
        const target = resolveFocusTarget({ newCol, newOp, effectiveType });

        formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        const fieldRef =
            target === 'operator' ? operatorSelectRef
            : target === 'value' ? valueFieldRef
            : columnSelectRef;
        focusAntField(fieldRef);
    }, [newCol, newOp, effectiveType]);

    useEffect(() => {
        if (!active) return undefined;

        const timer = window.setTimeout(alignAndFocus, FOCUS_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [active, newCol, newOp, effectiveType, focusGeneration, alignAndFocus]);

    return { formSectionRef, columnSelectRef, operatorSelectRef, valueFieldRef };
}
